import PropTypes from 'prop-types';
import React, {
  useState,
  useEffect,
} from 'react';

import useTownAssets from '../stores/townAssets/useTownAssets';
import config from '../utils/config';
import reportCrash from '../utils/reportCrash';

import SupportLink from './SupportLink';

//
// Do the following to trigger a fake error in dev/demo in the console:
// window.__triggerReactError()
//
function DevCrashTrigger() {
  const [shouldCrash, setShouldCrash] = useState(false);

  useEffect(
    () => {
      if (config.dev || config.demo) {
        window.__triggerReactError = () => setShouldCrash(true);
      }
      return () => {
        delete window.__triggerReactError;
      };
    },
    [],
  );

  if (shouldCrash) throw new Error('Test ErrorBoundary');
  return null;
}

const isChunkLoadError = (error) => (
  /dynamically imported module|loading chunk/i.test(error?.message)
);

function CrashFallback() {
  const { assets: { translate } } = useTownAssets();

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100vh',
      fontFamily: 'sans-serif',
      textAlign: 'center',
      padding: '2rem',
    }}
    >
      <img src="/images/svg/gui/deadComputer.svg" alt="Error" style={{ height: '5rem', marginBottom: '2rem' }} />
      <h1 style={{ fontSize: '1.25rem', marginBottom: '0.5rem' }}>
        {translate('errorBoundaryTitle')}
      </h1>
      <p style={{ color: '#666' }}>
        {translate('errorBoundaryRefreshHint')}
      </p>
      <div style={{ marginTop: '2rem' }}>
        <SupportLink />
      </div>
    </div>
  );
}

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, isChunkError: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, isChunkError: isChunkLoadError(error) };
  }

  componentDidCatch(error) {
    const { isChunkError } = this.state;
    if (isChunkError) {
      const reloadKey = 'px_chunk_reload';
      const lastReload = sessionStorage.getItem(reloadKey);
      const now = Date.now();

      if ((!lastReload) || ((now - Number(lastReload)) > 10000)) {
        sessionStorage.setItem(reloadKey, String(now));
        window.location.reload();
        return;
      }
      // Loop guard triggered — show error page instead
      this.setState({ isChunkError: false });
    }

    reportCrash(error);
  }

  render() {
    const { hasError, isChunkError } = this.state;
    const { children } = this.props;

    if (isChunkError) {
      // About to reload — render nothing
      return null;
    }

    if (hasError) {
      return <CrashFallback />;
    }

    return (
      <>
        {(config.dev || config.demo) && <DevCrashTrigger />}
        {children}
      </>
    );
  }
}

ErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired,
};

export default ErrorBoundary;
